import {
  AssetType,
  sideBarWidthAtom,
  iframeFocusModeSetupSetter,
} from "@zapier/canvas-state";
import {
  PRODUCT_NAV_WIDTH,
  FOCUS_MODE_CONTAINER_RADIUS,
} from "@zapier/canvas-constants";
import { Button, Spacer, Spinner, Tag, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import classNames from "classnames";

import { getButtonNameByTheAssetType } from "../../utils/getButtonNameByTheAssetType";
import { getIconNameByAssetType } from "../../utils/getIconNameByAssetType";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";

import styles from "./FocusModeContainer.module.css";

interface FocusModeContainerProps {
  assetType: AssetType;
  onOverlayClick: (e: React.MouseEvent) => void;
  title: string;
  url: string | undefined;
  disableIframe?: boolean;
  onClose: () => void;
}

export function FocusModeContainer({
  onOverlayClick,
  title,
  url,
  assetType,
  disableIframe = false,
  onClose,
}: FocusModeContainerProps) {
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();
  const [isLoading, setIsLoading] = useState(true);
  const setupIframeFocusMode = useSetAtom(iframeFocusModeSetupSetter);

  const checkForErrorContent = useCallback((iframe: HTMLIFrameElement) => {
    try {
      // Access iframe content (works because it's same-origin)
      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        const bodyText = iframeDoc.body?.textContent || "";

        if (
          bodyText.includes("Unable to access Zap") ||
          bodyText.includes("You don't have access") ||
          bodyText.includes("Permission denied") ||
          bodyText.includes("Access forbidden") ||
          bodyText.includes("no longer exists")
        ) {
          setIsLoading(false);
          return true;
        }
      }
    } catch (error) {
      console.error("Could not access iframe content:", error);
    }
    return false;
  }, []);

  const handleIframeLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>, assetType: AssetType) => {
      const iframe = event.currentTarget;

      if (url) {
        // Zap is the only one asset that you can preview when it's private
        // So we need to check for error content on the same-origin iframe
        // Other assets are "locked" and you can't preview them when they're private
        // Currently, tyring to align with product to lock Zaps as well, so we will remove this check once we do that
        if (assetType === "zap") {
          const hasError = checkForErrorContent(iframe);

          if (hasError) {
            return;
          }
        }

        setupIframeFocusMode({
          iframe,
          onReady: () => {
            setIsLoading(false);
          },
        });
      }
    },
    [url, setupIframeFocusMode, checkForErrorContent],
  );

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Use sideBarWidth instead of isSidebarOpen to avoid timing issues
  const getBorderRadius = (sideBarWidth: number) =>
    sideBarWidth > PRODUCT_NAV_WIDTH
      ? "0px"
      : `${FOCUS_MODE_CONTAINER_RADIUS}px`;

  const handleGoToAssetClick = useCallback(() => {
    if (!url) return;

    try {
      const editUrl = new URL(url, window.location.origin);
      editUrl.searchParams.delete("is_view_mode");

      if (assetType === "zap") {
        editUrl.searchParams.set(
          "copilot_prompt",
          "Help me finish setting up my Zap",
        );
      }

      // Emit event for focus mode asset opening
      if (assetType) {
        emitEventOnAssetOpen({
          asset: assetType,
          location: "focus mode",
        });
      }

      window.open(editUrl.toString(), "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error(`Failed to open ${assetType} URL:`, error);
    }
  }, [url, assetType, emitEventOnAssetOpen]);

  return (
    <div
      className={styles.overlay}
      style={{
        left: `${sideBarWidth}px`,
        borderTopLeftRadius: getBorderRadius(sideBarWidth),
      }}
      onClick={onOverlayClick}
    >
      <div
        className={styles.container}
        style={{
          borderTopLeftRadius: getBorderRadius(sideBarWidth),
        }}
      >
        <div
          className={styles.header}
          style={{
            borderTopLeftRadius: getBorderRadius(sideBarWidth),
          }}
        >
          <div className={styles.leftSection}>
            <Button
              variant="tertiary"
              size="small"
              iconPosition="start"
              onPress={onClose}
              className={styles.backButton}
            >
              <Icon name="arrowBigLeft" />
              Back to Canvas
            </Button>

            <Spacer width={8} />
            <Icon
              name={getIconNameByAssetType(assetType)}
              size={16}
              color="BrandOrange"
            />
            <Spacer width={4} />
            <Text type="Body1" fontWeight={400} className={styles.titleText}>
              {title}
            </Text>
            <Spacer width={8} />
            <Tag size="small">Preview</Tag>
          </div>

          <div className={styles.rightSection}>
            <Button
              variant="primary"
              size="small"
              onPress={handleGoToAssetClick}
              isDisabled={!url}
            >
              {getButtonNameByTheAssetType(assetType)}
            </Button>
          </div>
        </div>

        {/* Show loader while waiting for iframe to be ready */}
        {isLoading && url && (
          <div className={styles.loaderContainer}>
            <div className={styles.loader}>
              <Spinner size="small" />
              <Spacer height={8} />
              <Text type="Body1" color="TextWeakest">
                Loading preview...
              </Text>
            </div>
          </div>
        )}

        {url && (
          <iframe
            title={title}
            src={url}
            className={classNames(styles.iframe, {
              [styles.iframeHidden]: isLoading,
            })}
            sandbox={
              disableIframe ? "allow-same-origin allow-scripts" : undefined
            }
            onLoad={(event) => handleIframeLoad(event, assetType)}
            onError={handleIframeError}
          />
        )}
      </div>
    </div>
  );
}
